// src/lib/rtc.js — PeerManager (mediasoup SFU) compatibel met jouw App.js
import { socket } from "./sessionApi";
import * as mediasoupClient from "mediasoup-client";

// Kleine helper om veilig alle tracks te stoppen
function stopTracks(stream) {
  if (!stream) return;
  try {
    stream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {}
    });
  } catch {}
}

class PeerManager {
  constructor({ getLocalStream, onPeerStream, onPeerRemove, iceServers }) {
    this.getLocalStream = getLocalStream || (() => null);
    this.onPeerStream = onPeerStream || (() => {});
    this.onPeerRemove = onPeerRemove || (() => {});
    this.iceServers = iceServers || [];


    this.device = null;
    this.sendT = null;
    this.recvT = null;


    this.localStream = null;
    this.producers = new Map(); // kind -> producer
    this.consumers = new Map(); // producerId -> { consumer, peerId, kind }


    // samengestelde streams per peer (audio+video samen)
    this.peerStreams = new Map(); // peerId -> MediaStream
    this.prodToPeer = new Map();  // producerId -> peerId
  }

    // Nieuwe helper: lokale stream + producers opruimen
  stopLocalStream() {
    if (this.localStream) {
      stopTracks(this.localStream);
      this.localStream = null;
    }

    for (const p of this.producers.values()) {
      try {
        p.close();
      } catch {}
    }
    this.producers.clear();
  }


  wireSocketHandlers() {
    socket.on("rtc:new-producer", async ({ peerId, producerId }) => {
      this.prodToPeer.set(producerId, peerId);
      try { await this._consume(producerId); } catch {}
    });


    // compat cleanup bij peer leave (simple-peer pad)
   socket.on("rtc:peer-left", ({ peerId }) => {
  if (!peerId) return;
  this._removePeer(peerId);
  this.onPeerRemove?.(peerId);
});
  }


  async joinRoom() {
    const joinRes = await new Promise((res) =>
      socket.emit("rtc:join", {}, (r) => res(r))
    );
    if (!joinRes?.ok) throw new Error(joinRes?.error || "rtc:join failed");


    this.device = new mediasoupClient.Device();
    await this.device.load({ routerRtpCapabilities: joinRes.rtpCapabilities });


    await this._createTransports();


    const ls = this.getLocalStream?.();
    if (ls) await this.setLocalStream(ls);


    const list = Array.isArray(joinRes.producers) ? joinRes.producers : [];
    for (const { producerId, peerId } of list) {
      this.prodToPeer.set(producerId, peerId);
      try { await this._consume(producerId); } catch {}
    }
  }


  _removePeer(peerId) {
  // verwijder consumers
  for (const [prodId, info] of [...this.consumers.entries()]) {
    if (info.peerId === peerId) {
      try { info.consumer.close(); } catch {}
      this.consumers.delete(prodId);
    }
  }

  // verwijder stream
  if (this.peerStreams.has(peerId)) {
    this.peerStreams.delete(peerId);

    // 🔥 CRUCIAAL: UI vertellen dat stream weg is
    this.onPeerStream?.(peerId, null);
  }
}



  async _createTransports() {
    // SEND
    const sendP = await new Promise((res) =>
      socket.emit("rtc:createTransport", { direction: "send" }, (r) => res(r))
    );
    if (sendP?.error) throw new Error(sendP.error);
    this.sendT = this.device.createSendTransport(sendP);
    this.sendT.on("connect", ({ dtlsParameters }, cb, errCb) => {
      socket.emit(
        "rtc:connectTransport",
        { transportId: this.sendT.id, dtlsParameters },
        (r) => (r?.ok ? cb() : errCb(r?.error || "connect send failed"))
      );
    });
    this.sendT.on("produce", ({ kind, rtpParameters, appData }, cb, errCb) => {
      socket.emit(
        "rtc:produce",
        { transportId: this.sendT.id, kind, rtpParameters, appData },
        ({ id, error }) => (error ? errCb(error) : cb({ id }))
      );
    });


    // RECV
    const recvP = await new Promise((res) =>
      socket.emit("rtc:createTransport", { direction: "recv" }, (r) => res(r))
    );
    if (recvP?.error) throw new Error(recvP.error);
    this.recvT = this.device.createRecvTransport(recvP);
    this.recvT.on("connect", ({ dtlsParameters }, cb, errCb) => {
      socket.emit(
        "rtc:connectTransport",
        { transportId: this.recvT.id, dtlsParameters },
        (r) => (r?.ok ? cb() : errCb(r?.error || "connect recv failed"))
      );
    });
  }


    // robuuste versie (fix “track ended” + veilig bij null)
  async setLocalStream(stream) {
    const isLive = (t) => t && t.readyState === "live";

    // 🔴 Belangrijk: als stream null/undefined is → alles uit,
    // GEEN nieuwe getUserMedia-aanvraag.
    if (!stream) {
      this.stopLocalStream();
      return;
    }

    let audio = stream.getAudioTracks().find(isLive) || null;
    let video = stream.getVideoTracks().find(isLive) || null;

    // Alleen fallback als we WEL een stream-object hebben, maar GEEN live tracks
    if (!audio && !video && stream) {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        stream = s;
        audio = s.getAudioTracks().find(isLive) || null;
        video = s.getVideoTracks().find(isLive) || null;
      } catch (e) {
        console.warn("[PeerManager] getUserMedia fallback faalde:", e);
      }
    }

    // oude producers sluiten
    for (const p of this.producers.values()) {
      try {
        p.close();
      } catch {}
    }
    this.producers.clear();

    this.localStream = stream;

    if (!this.sendT) return;

    if (audio) {
      try {
        const pa = await this.sendT.produce({ track: audio });
        this.producers.set("audio", pa);
        audio.addEventListener("ended", () => {
          try {
            pa.close();
          } catch {}
          this.producers.delete("audio");
        });
      } catch (e) {
        console.warn("[PeerManager] produce audio mislukte:", e);
      }
    }

    if (video) {
      try {
        const pv = await this.sendT.produce({
          track: video,
          encodings: [
            { maxBitrate: 150_000 },
            { maxBitrate: 600_000 },
            { maxBitrate: 1_500_000 },
          ],
          codecOptions: { videoGoogleStartBitrate: 1000 },
        });
        this.producers.set("video", pv);
        video.addEventListener("ended", () => {
          try {
            pv.close();
          } catch {}
          this.producers.delete("video");
        });
      } catch (e) {
        console.warn("[PeerManager] produce video mislukte:", e);
      }
    }
  }



  async addLocalTracksToAll() {
    if (this.localStream) await this.setLocalStream(this.localStream);
  }


  // NIEUW: consume met “nieuwe streamreferentie” bij elke update (voorkomt freeze; audio+video samen)
  async _consume(producerId) {
    if (!this.recvT || !this.device) return;


    const params = await new Promise((res) =>
      socket.emit(
        "rtc:consume",
        {
          transportId: this.recvT.id,
          producerId,
          rtpCapabilities: this.device.rtpCapabilities,
        },
        (r) => res(r)
      )
    );
    if (!params || params.error) return;


    const consumer = await this.recvT.consume({
      id: params.id,
      producerId: params.producerId,
      kind: params.kind,
      rtpParameters: params.rtpParameters,
    });


    const peerId = params.peerId || this.prodToPeer.get(producerId) || "peer";
    this.consumers.set(producerId, { consumer, peerId, kind: params.kind });


    // maak elke update een NIEUWE MediaStream met alle tracks (triggert video.play())
    const existing = this.peerStreams.get(peerId) || new MediaStream();
    const tracks = [
      ...existing.getTracks().filter((t) => t.kind !== consumer.kind),
      consumer.track,
    ];
    const merged = new MediaStream(tracks);
    this.peerStreams.set(peerId, merged);


    // cleanup hooks
    const removeFromMap = () => {
      const cur = this.peerStreams.get(peerId);
      if (!cur) return;
      const left = cur.getTracks().filter(
        (t) => t.id !== consumer.track.id
      );
      const next = new MediaStream(left);
      if (left.length) this.peerStreams.set(peerId, next);
      else this.peerStreams.delete(peerId);
      this.onPeerStream?.(peerId, next); // push update naar UI
    };
    consumer.track.addEventListener?.("ended", removeFromMap);
    consumer.on("transportclose", () => {
      try {
        consumer.close();
      } catch {}
      removeFromMap();
    });


    // eerst resume, dan doorgeven — minder kans op “stil” blijven
    try {
      await new Promise((res) =>
        socket.emit("rtc:resume", { consumerId: consumer.id }, () => res(true))
      );
    } catch {}


    // push update naar UI (nieuwe referentie triggert <video>.play())
    this.onPeerStream?.(peerId, merged);
  }


   destroy() {
    // eerst lokale stream + producers netjes opruimen
    this.stopLocalStream();

    try {
      for (const { consumer } of this.consumers.values()) consumer.close();
    } catch {}
    try {
      this.sendT?.close();
    } catch {}
    try {
      this.recvT?.close();
    } catch {}

    this.consumers.clear();
    this.peerStreams.clear();
  }

}


export default PeerManager;
export { PeerManager };
